import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from "react";

import { useEnvContext } from "./EnvironmentContext";
import { useUrlContext } from "./urlContext";
import { useConsoleLog } from "../hooks/consoleLog";
import { DEFAULT_MIN_VARIANT, DEFAULT_MAX_VARIANT, MAX_VARIANT_BY_FLOW } from "../constants/abTests";
import Loader from "../templates/Loader/Loader";
import { hasMosCfg } from "../utils/urlParams";

interface DevModeState {
  devModeVariant: number | null;
  stagingEventsStatus: boolean;
  emulateUnexpectError: boolean;
  gameSupport: boolean;
}

interface DevModeContextType extends DevModeState {
  generateNewVariant: () => void;
  setStagingEventsStatus: (status: boolean) => void;
  unexpectError: () => void;
  supportGame: () => void;
}

const DevMode = createContext<DevModeContextType | undefined>(undefined);

export const useDevMode = (): DevModeContextType => {
  const context = useContext(DevMode);
  if (!context) {
    throw new Error("useDevMode must be used within DevModeProvider");
  }
  return context;
};

const DEV_MODE_VARIANT_KEY = "MemoryOSDevModeVariant";
const STAGING_EVENTS_KEY = "MemoryOSstagingEvent";

interface DevModeProviderProps {
  children: ReactNode;
}

export const DevModeProvider = ({ children }: DevModeProviderProps) => {
  const { isProduction } = useEnvContext();
  const { currentFlow } = useUrlContext();
  const logger = useConsoleLog();

  const maxVariant = (currentFlow && MAX_VARIANT_BY_FLOW[currentFlow]) || DEFAULT_MAX_VARIANT;

  const [isDevModeReady, setIsDevModeReady] = useState(false);

  const [devModeState, setDevModeState] = useState<DevModeState>({
    devModeVariant: null,
    stagingEventsStatus: false,
    emulateUnexpectError: false,
    gameSupport: false,
  });

  const generateRandomNumber = (max: number, min: number): number => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };

  const getInitialStagingStatus = useCallback((): boolean => {
    if (isProduction) return false;

    try {
      const storedValue = localStorage.getItem(STAGING_EVENTS_KEY);
      if (storedValue === null) return false;
      return JSON.parse(storedValue);
    } catch (error) {
      logger.error("Error reading from localStorage:", error);
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProduction]);

  const initializeDevMode = useCallback(() => {
    let variant: number | null = null;

    const hasMosConfig = hasMosCfg();

    if (!isProduction && !hasMosConfig) {
      const storedVariant = localStorage.getItem(DEV_MODE_VARIANT_KEY);
      if (storedVariant) {
        variant = Number(storedVariant);
      } else {
        variant = generateRandomNumber(maxVariant, DEFAULT_MIN_VARIANT);
        localStorage.setItem(DEV_MODE_VARIANT_KEY, variant.toString());
      }
    } else {
      localStorage.removeItem(DEV_MODE_VARIANT_KEY);
    }

    setDevModeState({
      devModeVariant: variant,
      stagingEventsStatus: getInitialStagingStatus(),
      emulateUnexpectError: false,
      gameSupport: false,
    });

    setIsDevModeReady(true);
  }, [isProduction, getInitialStagingStatus, maxVariant]);

  useEffect(() => {
    initializeDevMode();
  }, [initializeDevMode]);

  useEffect(() => {
    if (devModeState.devModeVariant !== null) {
      logger.devMode(`Current devModeVariant: ${devModeState.devModeVariant}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devModeState.devModeVariant]);

  useEffect(() => {
    try {
      if (isProduction) {
        setDevModeState((prev) => ({ ...prev, stagingEventsStatus: false }));
        localStorage.removeItem(STAGING_EVENTS_KEY);
        return;
      }

      localStorage.setItem(
        STAGING_EVENTS_KEY,
        JSON.stringify(devModeState.stagingEventsStatus)
      );
      logger.warnInfo("Staging events status:", devModeState.stagingEventsStatus);
    } catch (error) {
      logger.critical("Staging event:", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProduction, devModeState.stagingEventsStatus]);

  const generateNewVariant = useCallback(() => {
    if (!isProduction) {
      const current = devModeState.devModeVariant ?? 0;
      const newVariant = (current % maxVariant) + DEFAULT_MIN_VARIANT;
      setDevModeState((prev) => ({ ...prev, devModeVariant: newVariant }));
      localStorage.setItem(DEV_MODE_VARIANT_KEY, newVariant.toString());
      logger.devMode(`New devModeVariant generated: ${newVariant}`);
    } else {
      logger.warn(`Cannot generate new variant in production mode.`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProduction, devModeState.devModeVariant, maxVariant]);

  const setStagingEventsStatus = useCallback((status: boolean) => {
    setDevModeState((prev) => ({ ...prev, stagingEventsStatus: status }));
  }, []);

  const unexpectError = useCallback(() => {
    setDevModeState((prev) => ({
      ...prev,
      emulateUnexpectError: !prev.emulateUnexpectError,
    }));
  }, []);

  const supportGame = useCallback(() => {
    setDevModeState((prev) => ({
      ...prev,
      gameSupport: !prev.gameSupport,
    }));
  }, []);

  const contextValue: DevModeContextType = {
    ...devModeState,
    generateNewVariant,
    setStagingEventsStatus,
    unexpectError,
    supportGame,
  };

  if (!isDevModeReady) {
    return <Loader />;
  }

  return <DevMode.Provider value={contextValue}>{children}</DevMode.Provider>;
};
